/**
 * CronPicker — schedule cadence authoring UI (ADR-0072).
 *
 * Wraps the meeting-style RecurrenceEditor in `mode="cron"`. Emits a
 * `{ cron, start_date, start_time }` triplet upward so the parent can
 * persist the user-entered datetime separately from the cron pattern (the
 * cron alone can't round-trip the start time for short intervals).
 */
import { useEffect, useMemo, useState } from "react";

import cronstrue from "cronstrue";
import * as cronParser from "cron-parser";

import { RecurrenceEditor, EMPTY_RECURRENCE } from "./RecurrenceEditor.js";
import type { RecurrenceValue } from "./RecurrenceEditor.js";
import {
  canRoundTripToForm,
  compileRecurrenceToCron,
  seedRecurrenceFromCron,
} from "../lib/cronCompile.js";

const { parseExpression } = (cronParser as {
  parseExpression: typeof import("cron-parser").parseExpression;
} & {
  default?: { parseExpression: typeof import("cron-parser").parseExpression };
}).default ?? cronParser;

export interface CronPickerValue {
  cron: string | null;
  start_date: string;
  start_time: string;
}

interface CronPickerProps {
  value: CronPickerValue;
  onChange: (next: CronPickerValue) => void;
  tenantTz: string;
}

export function CronPicker({ value, onChange, tenantTz }: CronPickerProps) {
  const [recurrence, setRecurrence] = useState<RecurrenceValue>(() =>
    seedRecurrenceFromValue(value, tenantTz)
  );

  // A cron authored outside this form (API, MCP door) may use features the
  // builder can't express. Seeding would silently fall back to defaults and
  // a save would replace the real cadence — so lock the builder behind an
  // explicit "Replace cadence" choice and show the stored cadence instead.
  const [cadenceReplaced, setCadenceReplaced] = useState(false);
  const locked = !cadenceReplaced && !!value.cron && !canRoundTripToForm(value.cron);

  // Re-seed when the parent passes a new value (e.g. loading an existing schedule).
  useEffect(() => {
    setRecurrence(seedRecurrenceFromValue(value, tenantTz));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.cron, value.start_date, value.start_time]);

  function handleRecurrenceChange(next: RecurrenceValue) {
    setRecurrence(next);
    onChange({
      cron: compileRecurrenceToCron(next),
      start_date: next.start_date,
      start_time: next.start_time,
    });
  }

  const compiled = useMemo(() => compileRecurrenceToCron(recurrence), [recurrence]);

  // While locked, every description is rendered from the STORED cron — the
  // builder state is a fabricated default and must never be displayed.
  const shownCron = locked ? value.cron! : compiled;

  const summary = useMemo(() => {
    try {
      return cronstrue.toString(shownCron, { use24HourTimeFormat: false, verbose: true });
    } catch {
      return null;
    }
  }, [shownCron]);

  const fires = useMemo(() => {
    try {
      const expr = parseExpression(shownCron, { tz: tenantTz, iterator: false });
      const out: Date[] = [];
      for (let i = 0; i < 3; i++) out.push(expr.next().toDate());
      return out;
    } catch {
      return null;
    }
  }, [shownCron, tenantTz]);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {locked ? (
        <div style={{ display: "grid", gap: 8 }}>
          <div className="interactionWarning" style={{ marginBottom: 0 }}>
            This schedule's cadence was authored outside this editor and can't
            be shown in the form below. Its current cadence is described under
            "Runs". To keep it, leave this section alone — the other fields on
            this dialog can still be edited and saved safely.
          </div>
          <div className="actions" style={{ justifyContent: "flex-start" }}>
            <button
              className="btn"
              type="button"
              onClick={() => {
                // Replacing is a real action: push the builder's state up
                // immediately so the summary below always describes what
                // Save will persist — never a mix of stored and default.
                setCadenceReplaced(true);
                handleRecurrenceChange(recurrence);
              }}
            >
              Replace cadence…
            </button>
          </div>
        </div>
      ) : (
        <RecurrenceEditor
          mode="cron"
          value={recurrence}
          onChange={handleRecurrenceChange}
        />
      )}

      <div aria-live="polite" style={{ display: "grid", gap: 6 }}>
        {summary ? (
          <div className="help" style={{ color: "var(--text)" }}>
            <strong>Runs:</strong> {summary}
          </div>
        ) : null}

        {fires && fires.length > 0 ? (
          <div className="help">
            <strong>Next 3 fires ({tenantTz}):</strong>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
              {fires.map((d, i) => (
                <li key={i}>{formatInTz(d, tenantTz)}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function seedRecurrenceFromValue(value: CronPickerValue, tenantTz: string): RecurrenceValue {
  const seeded = value.cron ? seedRecurrenceFromCron(value.cron) : null;
  const base = seeded ?? { ...EMPTY_RECURRENCE };
  return {
    ...base,
    timezone: tenantTz,
    start_date: value.start_date || base.start_date,
    start_time: value.start_time || base.start_time,
  };
}

function formatInTz(d: Date, tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}
